import { FC, useMemo, useState } from 'react'
import classNames from 'classnames/bind'
import { Icon, Loader, RoundedButton, SearchInput, SelectSearch } from '../../shared'
import { BottomPanel } from '../../shared/bottomPanel/BottomPanel'
import createSearchPanel from '../../../containers/SearchPanel'
import { useGetUserSessionsHistory } from '../../../hooks/queries/userSessions'
import { isDefined } from '@follow/cdk'
import { SessionHistoryEvent, SessionHistoryStatusFilter } from '../../../model/UserSession'
import {
  UnmountTransitionProps,
  withUnmountTransition,
} from '../../../misc/withUnmountTransition.hoc'
import { ComponentUsingBottomPanelProps } from '../../shared/bottomPanel/BottomPanel/BottomPanel.model'
import { getV3ColorCssVariable } from '../../../design-system/colors_v3'
import {
  filterEventsByStatus,
  HISTORY_RETENTION_NOTICE,
  mapSessionHistoryListItems,
  SessionHistoryListItem,
  sortByTimeDesc,
  STATUS_FILTER_OPTIONS,
} from './LoginHistoryBottomPanel.model'
import { LoginHistoryPanelItem } from './LoginHistoryPanelItem'
import styles from './LoginHistoryBottomPanel.module.scss'

const cx = classNames.bind(styles)
const SearchPanel = createSearchPanel<SessionHistoryListItem>()

interface LoginHistoryBottomPanelProps
  extends ComponentUsingBottomPanelProps,
    UnmountTransitionProps {}

const LoginHistoryBottomPanelComponent: FC<LoginHistoryBottomPanelProps> = ({
  isUnmounting,
  onRequestClose,
}) => {
  const [status, setStatus] = useState<SessionHistoryStatusFilter>(SessionHistoryStatusFilter.All)
  const [search, setSearch] = useState('')

  const { query, events } = useGetUserSessionsHistory({
    enabled: !isUnmounting,
  })

  const statusValue = STATUS_FILTER_OPTIONS.find((option) => option.value === status)

  const visibleEvents = useMemo(
    () =>
      mapSessionHistoryListItems(
        sortByTimeDesc(filterEventsByStatus(events, status)).filter((event) => {
          if (!search.trim()) {
            return true
          }

          const normalizedSearch = search.trim().toLowerCase()
          return matchesSearch(event, normalizedSearch)
        }),
      ),
    [events, search, status],
  )

  const pageCount = useMemo(() => {
    if (!query.data) {
      return 1
    }

    return query.hasNextPage ? query.data.pages.length + 1 : query.data.pages.length
  }, [query.data, query.hasNextPage])

  return (
    <BottomPanel
      testId="login_history"
      title="Historique des connexions"
      isUnmounting={isUnmounting}
      onRequestClose={onRequestClose}
    >
      {query.isError ? (
        <div className={cx('listArea')}>
          <div className={cx('stateError')} data-test-id="login_history-error">
            <Icon
              icon="warningTriangle"
              size="mega"
              color={getV3ColorCssVariable('shades', 'shade4')}
            />
            <div>
              <p className={cx('stateErrorTitle')}>
                Impossible de charger l&apos;historique des connexions
              </p>
              <p className={cx('stateErrorMessage')}>
                Une erreur est survenue. Vérifiez votre connexion et réessayez.
              </p>
            </div>
            <RoundedButton
              label="Réessayer"
              theme="primary"
              icon="reload"
              onClick={() => query.refetch()}
              testId="button-login_history-retry"
            />
          </div>
        </div>
      ) : (
        <>
          <SearchPanel
            items={visibleEvents}
            loading={query.isLoading}
            pageCount={pageCount}
            itemCount={visibleEvents.length}
            getItems={() => {
              if (query.hasNextPage && !query.isFetchingNextPage) {
                void query.fetchNextPage()
              }
            }}
            filterCapabilities={[]}
            renderActions={() => null}
            renderCustomFilters={() => (
              <div className={cx('filters')}>
                <div className={cx('filterField')}>
                  <SelectSearch<SessionHistoryStatusFilter>
                    value={statusValue}
                    options={STATUS_FILTER_OPTIONS}
                    onSelect={(option) => {
                      if (isDefined(option)) {
                        setStatus(option.value)
                      }
                    }}
                    colorPreset="dark"
                    clearable={false}
                    testId="select_search-login_history-status"
                  />
                </div>
                <div className={cx('filterField')}>
                  <SearchInput
                    value={search}
                    onChange={({ target: { value } }) => setSearch(value)}
                    colorPreset="dark"
                    placeholder="Adresse IP"
                    testId="input-login_history-search"
                  />
                </div>
              </div>
            )}
            renderItem={(item) => <LoginHistoryPanelItem item={item} />}
            renderHeaderActions={() => null}
            testId="login_history"
          />
          <div className={cx('listFooter')}>
            {query.isFetchingNextPage && (
              <div className={cx('footerLoader')}>
                <Loader width={48} height={48} />
              </div>
            )}
            <p className={cx('retentionNotice')} data-test-id="login_history-retention">
              {HISTORY_RETENTION_NOTICE}
            </p>
          </div>
        </>
      )}
    </BottomPanel>
  )
}

const matchesSearch = (event: SessionHistoryEvent, normalizedSearch: string) => {
  const haystack = [event.ipAddress, event.type, event.error, getHistoryEventLabel(event)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  return haystack.includes(normalizedSearch)
}

const getHistoryEventLabel = (event: SessionHistoryEvent) => {
  switch (event.type) {
    case 'LOGIN':
      return 'connexion reussie'
    case 'LOGOUT':
      return 'deconnexion'
    case 'LOGIN_ERROR':
      return 'echec de connexion'
    case 'LOGOUT_ERROR':
      return 'echec de deconnexion'
    default:
      return String(event.type ?? '')
  }
}

const withUnmountHoc = withUnmountTransition(LoginHistoryBottomPanelComponent)

export { withUnmountHoc as LoginHistoryBottomPanel }
